import React, { useEffect } from 'react';

import {
    DOSSIER_TACHES_SLICE_NAME,
    dossierTachesSelector,
    useFormaliteService,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import {
    EmptyBox,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import { StoreDatatable } from '@europrocurement/flexy-datatable';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useDispatch } from 'react-redux';
import { useTasksTableHook } from '@europrocurement/l2d-modules';
import { REALM_RESP_FORMALIST, UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { useSnackbar } from 'notistack';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { DossierTachesApiObject } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type AppDispatch, useAppSelector } from '../../../../redux/types';

/**
 * Page de listing des taches liées aux dossiers de formalités.
 */
const ListeTachesDossiers: React.FunctionComponent = function () {
    const dispatch = useDispatch<AppDispatch>();
    const datasource = useAppSelector(dossierTachesSelector);
    const { getDossierTaches } = useFormaliteService();
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { getColumnsTachesDossier, getFiltersTachesDossier } = useTasksTableHook({
        apiTarget: 'formalite',
    });
    const { handleEditTask } = useTasksHook({ apiTarget: 'formalite' });
    const { utilisateurApi } = useUtilisateurService();
    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const isFormalistManager = keycloakService.checkRole(REALM_RESP_FORMALIST);
    const { enqueueSnackbar } = useSnackbar();

    // Chargement initial des données
    // Filtre les tâches de l'utilisateur connecté ou affiche l'ensemble des tâches pour un manager
    useEffect(() => {
        if (isFormalistManager) {
            dispatch(getDossierTaches({}));
        } else if (userIdentifier) {
            utilisateurApi.apiUtilisateursGetCollection({ userIdentifier }).then((response) => {
                const user = response.data['hydra:member'][0] ?? null;

                if (user && user.userIdentifier === userIdentifier) {
                    dispatch({
                        type: `${DOSSIER_TACHES_SLICE_NAME}/setmainFilter`,
                        payload: {
                            key: 'tacheAssignTo',
                            value: `formalite/user/${user.id}`,
                        },
                    });

                    // Displayed label on formalist filter
                    if (user.prenom && user.nom) {
                        dispatch({
                            type: `${DOSSIER_TACHES_SLICE_NAME}/setmainFilterLabel`,
                            payload: { key: 'tacheAssignTo', label: `${user.prenom} ${user.nom}` },
                        });
                    }

                    dispatch(getDossierTaches({}));
                } else {
                    enqueueSnackbar('Utilisateur non trouvé - identifiant non conforme', {
                        variant: 'error',
                    });
                }
            });
        } else {
            enqueueSnackbar('Utilisateur non trouvé', { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdentifier]);

    const onClickRow = (e: React.MouseEvent, task: DossierTachesApiObject) => {
        handleEditTask(task);
    };

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données, échec du chargement des tâches."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={6}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader breadcrumbPath={['Liste des tâches liées aux dossiers']} />
            {datasource && (
                <StoreDatatable
                    dataSource={datasource}
                    columns={getColumnsTachesDossier()}
                    fetchData={getDossierTaches}
                    filters={getFiltersTachesDossier()}
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={onClickRow}
                    observers={[]}
                />
            )}
        </SkeletonLoader>
    );
};

export default ListeTachesDossiers;
